
import { Component, OnInit, inject, signal, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { finalize } from 'rxjs';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { StyleClassModule } from 'primeng/styleclass';
import { CaptchaComponent } from '../../../../../common/components/captcha/captcha.component';
import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { CommonRoute } from '../../../../../common/enums/common-route.enum';
import { CaptchaMessage } from '../../../../../common/enums/captcha-message.enum';
import { AuthenticationService } from '../../../../../common/services/authentication/authentication.service';
import { LayoutService } from '../../../../layout/service/app.layout.service';
import { AppConfigService } from '../../../../../common/config/app.config.service';

@Component({
  selector: 'app-request-password-reset',
  standalone: true,
  templateUrl: './request-password-reset.component.html',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    StyleClassModule,
    CaptchaComponent
],
})
@ComponentAccess('RequestPasswordResetComponent')
export class RequestPasswordResetComponent implements OnInit {
  readonly layoutService = inject(LayoutService);
  private readonly fb = inject(FormBuilder);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly configService = inject(AppConfigService);
  private readonly messageService = inject(MessageService);

  recoveryForm: FormGroup;
  readonly recoverySuccess = signal<boolean>(false);
  /** From the moment Recover is clicked: the browser check alone can take seconds. */
  readonly isSubmitting = signal<boolean>(false);

  // Captcha related properties
  readonly isCaptchaRequired = signal<boolean>(false);
  private readonly captcha = viewChild(CaptchaComponent);

  constructor() {
    this.recoveryForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    // Determine if captcha is required based on env config and platform
    this.isCaptchaRequired.set(this.configService.isCaptchaEnabled);
  }

  async onSubmit(): Promise<void> {
    if (this.recoveryForm.valid && !this.isSubmitting()) {
      // Busy BEFORE the browser check, which can take seconds on its own.
      this.isSubmitting.set(true);

      // The captcha challenge runs on demand here (execute-on-submit), so
      // nothing refreshes or goes stale while the page sits open.
      const captchaOk = (await this.captcha()?.verify()) ?? false;
      if (this.isCaptchaRequired() && !captchaOk) {
        this.isSubmitting.set(false);
        this.messageService.add({
          severity: 'error',
          summary: CaptchaMessage.FailedSummary,
          detail: CaptchaMessage.Failed,
        });
        return;
      }

      const { email } = this.recoveryForm.value;
      this.authenticationService
        .recoverPassword(email)
        .pipe(finalize(() => this.isSubmitting.set(false)))
        .subscribe({
          next: () => {
            this.recoverySuccess.set(true);
          },
          error: () => {
            this.recoverySuccess.set(false);
          },
        });
    }
  }

  navigateToLogin(): void {
    this.router.navigate([CommonRoute.LOGIN]);
  }
}
